import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiOrgId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import { FetchEndpoint } from '#/utils/endpoint/FetchEndpoint';
import { keyBy } from '#/utils/functional/array/keyBy';

type ApiListingId =
  | ApiJobId
  | ApiInternshipId
  | ApiVolopId
  | ApiEventId
  | ApiOrgId;

type ApiResponse = {
  listings: {
    id: ApiListingId;
    applicationId: ApiListingApplicationId | null;
    saved: boolean;
    isOrgMember: boolean;
  }[];
};

type ListingExtras = Partial<
  Record<ApiListingId, ApiResponse['listings'][number]>
>;

function mapResponse(response: ApiResponse): ListingExtras {
  return keyBy(response.listings ?? [], 'id');
}

// The endpoint accepts up to 200 ids.
// We batch like that because of the mock we use for CI,
// that doesn't have pagination.
const MAX_LISTING_IDS_PER_REQUEST = 200;

const endpoint = new FetchEndpoint({
  urlFactory: (listingIds) => {
    if (listingIds.length === 0) return;
    return '/data/userdashboard/listing-extras';
  },
  optionsFactory: (listingIds: ApiListingId[]) => ({
    method: 'post',
    body: { listingIds },
  }),
  mapResponse,
});

// Exports

const fetchListingExtrasBatch = endpoint.client();

export async function userDashboardApiClientFetchListingExtras(
  listingIds: ApiListingId[],
): Promise<ListingExtras> {
  const batches: ApiListingId[][] = [];
  for (let i = 0; i < listingIds.length; i += MAX_LISTING_IDS_PER_REQUEST) {
    batches.push(listingIds.slice(i, i + MAX_LISTING_IDS_PER_REQUEST));
  }

  const results = await Promise.all(batches.map(fetchListingExtrasBatch));

  return Object.assign({}, ...results) as ListingExtras;
}

export const useUserDashboardApiFetchListingExtras = endpoint.hook();

export const userDashboardApiServerFetchListingExtras = endpoint.server();
